/**
 * CandidateVerdictExplainer.jsx — Slice β + Workspace W3.
 *
 * Inline expander rendered under a completed candidate row in
 * `RoleCandidatesPanel`. Lazy-fetches the existing
 * `GET /api/employer/v1/candidates/{id}/evaluation` envelope
 * (no new endpoint) and surfaces every role-relative signal the
 * substrate already persists — so the operator can see WHY the
 * verdict landed without leaving the row.
 *
 * Workspace W3 adds, vs Slice β baseline:
 *   • "Evaluated against this role" header (title + description)
 *   • per-required-criterion match grid (met / partial / missing)
 *   • per-dimension breakdown table with weights from criteria_snapshot
 *   • evidence-map block (one excerpt per dimension)
 *   • dealbreaker basis text (was: key-only chips → now: key + basis)
 *   • dossier evidence-used row (CV / Cover Letter / Supporting / Notes)
 *   • weighting-applied footnote ("default" vs "custom for this role")
 *   • doctrine footnote on contextual evidence vs CV facts
 *
 * Read-only. No endpoint changes. No schema changes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertTriangle, CheckCircle2, XCircle, Circle, MinusCircle,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';


const DIMENSION_DEFAULTS = {
  seniority:         25,
  domain_alignment:  30,
  evidence_strength: 25,
  location:          10,
  risk:              10,
};

const DIMENSION_LABELS = {
  seniority:         'Seniority',
  domain_alignment:  'Domain',
  evidence_strength: 'Evidence',
  location:          'Location',
  risk:              'Risk',
};


function verdictHeader(verdict) {
  const map = {
    shortlist: { cls: 'text-emerald-200 bg-emerald-500/15 border-emerald-500/40', label: 'Shortlist', icon: CheckCircle2 },
    review:    { cls: 'text-amber-200 bg-amber-500/15 border-amber-500/40',       label: 'Review',    icon: AlertTriangle },
    reject:    { cls: 'text-rose-200 bg-rose-500/15 border-rose-500/40',          label: 'Reject',    icon: XCircle },
  };
  const cfg = map[verdict] || { cls: 'text-slate-200 bg-slate-500/15 border-slate-500/40', label: '—', icon: AlertTriangle };
  const Icon = cfg.icon;
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${cfg.cls}`}
      data-testid="candidate-verdict-header"
    >
      <Icon className="h-4 w-4" />
      <span className="text-xs uppercase tracking-wider font-semibold">{cfg.label}</span>
    </div>
  );
}


function fitBar(score) {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const colour = v >= 75 ? 'bg-emerald-500' : v >= 45 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-3" data-testid="candidate-verdict-fit-bar">
      <div className="flex-1 h-2 rounded bg-ndor-bg/60 overflow-hidden">
        <div className={`h-full ${colour}`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-sm font-semibold text-ndor-ink">{v}</span>
      <span className="text-xs text-slate-300">/ 100</span>
    </div>
  );
}


function renderList(items, accentTone = 'emerald') {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-xs text-slate-300">—</p>;
  }
  const dotCls = accentTone === 'amber'
    ? 'text-amber-300'
    : 'text-emerald-300';
  return (
    <ul className="space-y-1.5 text-sm text-slate-100">
      {items.slice(0, 6).map((s, i) => (
        <li key={i} className="leading-relaxed flex gap-2">
          <span className={`${dotCls} shrink-0`} aria-hidden="true">•</span>
          <span>{String(s).slice(0, 240)}</span>
        </li>
      ))}
    </ul>
  );
}


function renderDealbreakers(flags) {
  if (!flags || typeof flags !== 'object') return null;
  const entries = Object.entries(flags);
  if (entries.length === 0) return null;
  const anyTriggered = entries.some(([, v]) => v === true || (v && typeof v === 'object' && v.triggered));
  return (
    <div
      className="space-y-2"
      data-testid="candidate-verdict-dealbreakers"
      data-any-triggered={anyTriggered ? 'true' : 'false'}
    >
      <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">Dealbreakers</p>
      <div className="space-y-1.5">
        {entries.map(([k, v]) => {
          const triggered = v === true || (v && typeof v === 'object' && v.triggered === true);
          const basis     = v && typeof v === 'object' ? (v.basis || v.reason || '') : '';
          return (
            <div
              key={k}
              className={`text-xs px-2.5 py-1.5 rounded border leading-relaxed ${
                triggered
                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                  : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
              }`}
            >
              <span className="font-medium">{triggered ? '✗' : '✓'} {k}</span>
              {basis ? <span className="ml-1 opacity-90"> · {String(basis).slice(0, 200)}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Per-required-criterion match grid.
 *
 * We don't have a hard mapping in the persisted payload; we approximate
 * "met / partial / missing" by token-overlap between each role
 * required_criteria entry and the union of all dimension_scores
 * positive_signals (met) / weaknesses (missing). Pure UI heuristic —
 * never affects what is stored or scored.
 */
function classifyCriterion(text, positives, negatives) {
  const t = (text || '').toLowerCase();
  if (!t) return 'partial';
  const tokens = Array.from(new Set(t.split(/[^a-z0-9]+/i).filter((w) => w.length >= 4)));
  if (tokens.length === 0) return 'partial';
  const inAny = (haystack) => tokens.some((tok) => haystack.toLowerCase().includes(tok));
  const positiveHit = positives.some(inAny);
  const negativeHit = negatives.some(inAny);
  if (positiveHit && !negativeHit) return 'met';
  if (negativeHit && !positiveHit) return 'missing';
  if (positiveHit && negativeHit) return 'partial';
  return 'partial';
}


function CriterionMatchRow({ text, status }) {
  const map = {
    met:     { cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200', Icon: CheckCircle2 },
    partial: { cls: 'border-amber-500/40 bg-amber-500/10 text-amber-200',       Icon: Circle },
    missing: { cls: 'border-rose-500/40 bg-rose-500/10 text-rose-200',          Icon: MinusCircle },
  };
  const { cls, Icon } = map[status] || map.partial;
  return (
    <div
      className={`flex items-start gap-2 text-xs px-2.5 py-2 rounded border ${cls}`}
      data-testid="candidate-verdict-criterion"
      data-match={status}
    >
      <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span className="leading-relaxed">{text}</span>
    </div>
  );
}


function DimensionRow({ name, score, weight }) {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const colour = v >= 75 ? 'bg-emerald-500' : v >= 45 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="grid grid-cols-[90px_1fr_44px_42px] gap-3 items-center text-xs">
      <span className="text-slate-200">{DIMENSION_LABELS[name] || name}</span>
      <div className="h-2 rounded bg-ndor-bg/60 overflow-hidden">
        <div className={`h-full ${colour}`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-right text-ndor-ink font-semibold">{v}</span>
      <span className="text-right text-xs text-slate-300">·{weight}%</span>
    </div>
  );
}


function dossierEvidencePills(strengths, gaps) {
  // v1 dossier — backend does not yet attribute excerpts to source.
  // CV is always present (it is the dossier's required core). The
  // other three sources are "—" (absent) until Dossier v2 ships.
  const present = (Array.isArray(strengths) && strengths.length > 0)
    || (Array.isArray(gaps) && gaps.length > 0);
  const pill = (label, isPresent) => (
    <span
      key={label}
      className={`text-xs px-2 py-0.5 rounded border ${
        isPresent
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
          : 'border-slate-500/40 bg-slate-500/10 text-slate-300'
      }`}
    >
      {label} {isPresent ? '✓' : '—'}
    </span>
  );
  return (
    <div className="space-y-2" data-testid="candidate-verdict-dossier-evidence">
      <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">Evidence used from dossier</p>
      <div className="flex flex-wrap gap-1.5">
        {pill('CV', present)}
        {pill('Cover Letter', false)}
        {pill('Supporting Statement', false)}
        {pill('Notes', false)}
      </div>
    </div>
  );
}


export default function CandidateVerdictExplainer({ candidateId, open, role = null }) {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/employer/v1/candidates/${candidateId}/evaluation`);
      setData(resp?.item || null);
    } catch (err) {
      setError(err?.message || 'Could not load evaluation details.');
    } finally {
      setBusy(false);
    }
  }, [candidateId]);

  useEffect(() => {
    if (open && !data && !busy && !error) load();
  }, [open, data, busy, error, load]);

  // Pull the criteria_snapshot frozen at evaluation time (preferred)
  // and fall back to the live role definition if the snapshot is
  // unavailable (older rows).
  const snapshot = data?.criteria_snapshot
    || data?.raw_runner_payload?.criteria_snapshot
    || (role?.structured_definition_json
        ? {
            title:               role.title,
            description:         role.structured_definition_json.description,
            required_criteria:   role.structured_definition_json.required_criteria
                                  || role.structured_definition_json.must_have || [],
            preferred_criteria:  role.structured_definition_json.preferred_criteria
                                  || role.structured_definition_json.nice_to_have || [],
            weighted_dimensions: role.structured_definition_json.weighted_dimensions,
          }
        : null);

  const dimensionScores = data?.dimension_scores
    || data?.raw_runner_payload?.dimension_scores
    || {};
  const evidenceMap = data?.evidence_map
    || data?.raw_runner_payload?.evidence_map
    || {};
  const positives = useMemo(() => {
    const out = [];
    Object.values(dimensionScores).forEach((dim) => {
      const ps = dim?.positive_signals;
      if (Array.isArray(ps)) ps.forEach((s) => out.push(String(s)));
    });
    return out;
  }, [dimensionScores]);
  const negatives = useMemo(() => {
    const out = [];
    Object.values(dimensionScores).forEach((dim) => {
      const ws = dim?.weaknesses;
      if (Array.isArray(ws)) ws.forEach((s) => out.push(String(s)));
    });
    return out;
  }, [dimensionScores]);

  const weights = useMemo(() => {
    const raw = snapshot?.weighted_dimensions;
    if (!raw || typeof raw !== 'object') {
      return { values: DIMENSION_DEFAULTS, isCustom: false };
    }
    const out = { ...DIMENSION_DEFAULTS };
    let touched = false;
    for (const k of Object.keys(DIMENSION_DEFAULTS)) {
      const n = Number(raw[k]);
      if (Number.isFinite(n) && n >= 0 && n <= 100) {
        out[k] = Math.round(n);
        if (Math.round(n) !== DIMENSION_DEFAULTS[k]) touched = true;
      }
    }
    return { values: out, isCustom: touched };
  }, [snapshot]);

  if (!open) return null;

  if (busy) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-slate-200"
           data-testid="candidate-verdict-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading evaluation details…
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2 py-3" data-testid="candidate-verdict-error">
        <div className="flex items-start gap-2 text-sm text-rose-300">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span>{error}</span>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs text-slate-200 hover:text-ndor-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-xs text-slate-300 py-3" data-testid="candidate-verdict-empty">
        No evaluation details available.
      </p>
    );
  }

  const required = Array.isArray(snapshot?.required_criteria)
    ? snapshot.required_criteria.slice(0, 8)
    : [];

  return (
    <div
      className="mt-4 space-y-5 rounded-lg border border-ndor-edge bg-ndor-bg/50 p-5"
      data-testid="candidate-verdict-explainer"
      data-candidate-id={candidateId}
    >
      {/* W3 — "Evaluated against this role" header */}
      <header
        className="space-y-1.5 pb-4 border-b border-ndor-edge"
        data-testid="candidate-verdict-role-header"
      >
        <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">
          Evaluated against this role
        </p>
        <p className="text-sm text-ndor-ink font-medium">
          {snapshot?.title || role?.title || '—'}
        </p>
        {snapshot?.description ? (
          <p className="text-xs text-slate-200 leading-relaxed">
            {String(snapshot.description).slice(0, 220)}
          </p>
        ) : null}
      </header>

      {/* Verdict header + confidence */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {verdictHeader(data.governance_verdict)}
        {data.confidence_low != null && data.confidence_high != null && (
          <span className="text-xs text-slate-200">
            Confidence {data.confidence_low}–{data.confidence_high}
          </span>
        )}
      </div>

      {data.overall_fit != null && fitBar(data.overall_fit)}

      {/* W3 — per-required-criterion match grid (heuristic, UI-only) */}
      {required.length > 0 && (
        <div className="space-y-2" data-testid="candidate-verdict-criteria-grid">
          <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">
            Required criteria match
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {required.map((c, i) => (
              <CriterionMatchRow
                key={i}
                text={String(c).slice(0, 120)}
                status={classifyCriterion(String(c), positives, negatives)}
              />
            ))}
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">
            Match status derived from positive signals and weaknesses recorded by the evaluator. Heuristic indicator only.
          </p>
        </div>
      )}

      {/* W3 — Dimension breakdown */}
      {Object.keys(dimensionScores).length > 0 && (
        <div className="space-y-2.5" data-testid="candidate-verdict-dimensions">
          <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">
            Dimension breakdown
          </p>
          <div className="space-y-2">
            {Object.keys(DIMENSION_DEFAULTS).map((k) => {
              const dim = dimensionScores[k];
              if (!dim) return null;
              const raw = typeof dim === 'object' ? (dim.score ?? dim.value ?? 0) : Number(dim);
              return (
                <DimensionRow
                  key={k}
                  name={k}
                  score={raw}
                  weight={weights.values[k]}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* W3 — Evidence map (one excerpt per dimension) */}
      {evidenceMap && typeof evidenceMap === 'object' && Object.keys(evidenceMap).length > 0 && (
        <div className="space-y-2" data-testid="candidate-verdict-evidence-map">
          <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">
            Evidence map
          </p>
          <ul className="space-y-1.5 text-sm text-slate-100">
            {Object.entries(evidenceMap).slice(0, 6).map(([dim, excerpt]) => (
              <li key={dim} className="leading-relaxed">
                <span className="text-xs uppercase tracking-wider text-slate-300 mr-2">
                  {DIMENSION_LABELS[dim] || dim}
                </span>
                <span className="text-xs text-emerald-300 mr-1.5">[CV]</span>
                <span>
                  {typeof excerpt === 'string'
                    ? excerpt.slice(0, 240)
                    : JSON.stringify(excerpt).slice(0, 240)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Verdict reason */}
      {data.verdict_reason && (
        <div className="space-y-1.5">
          <p className="text-xs uppercase tracking-wider text-slate-200 font-medium">Verdict reason</p>
          <p className="text-sm text-ndor-ink leading-relaxed whitespace-pre-line">
            {String(data.verdict_reason).slice(0, 600)}
          </p>
        </div>
      )}

      {/* Dealbreakers with basis text */}
      {renderDealbreakers(data.dealbreaker_flags)}

      {/* Strengths / gaps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-emerald-300 font-medium">Strengths</p>
          {renderList(data.strengths, 'emerald')}
        </div>
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-amber-300 font-medium">Gaps</p>
          {renderList(data.gaps, 'amber')}
        </div>
      </div>

      {/* W3 — dossier evidence pills */}
      {dossierEvidencePills(data.strengths, data.gaps)}

      {/* Legend + weighting + doctrine footnotes */}
      <div className="space-y-1.5 pt-4 border-t border-ndor-edge">
        <p
          className="text-xs text-slate-200 leading-relaxed"
          data-testid="candidate-verdict-legend"
        >
          Verdict rule:&nbsp;
          <span className="text-emerald-300 font-medium">≥75 Shortlist</span> ·&nbsp;
          <span className="text-amber-300 font-medium">45–74 Review</span> ·&nbsp;
          <span className="text-rose-300 font-medium">&lt;45 Reject</span> ·&nbsp;
          any dealbreaker triggered → Reject.
        </p>
        <p
          className="text-xs text-slate-300 leading-relaxed"
          data-testid="candidate-verdict-weighting-footnote"
        >
          Weighting applied:&nbsp;
          {Object.entries(weights.values).map(([k, v], i, arr) => (
            <span key={k}>
              {DIMENSION_LABELS[k]} {v}{i < arr.length - 1 ? ' · ' : ''}
            </span>
          ))}
          &nbsp;({weights.isCustom ? 'custom for this role' : 'NDOR default'}).
        </p>
        <p
          className="text-xs text-slate-300 leading-relaxed"
          data-testid="candidate-verdict-doctrine-footnote"
        >
          Numeric scores are anchored in CV / resume evidence. Cover letters,
          supporting statements and notes inside the dossier strengthen
          interpretation but do not override CV facts.
        </p>
      </div>
    </div>
  );
}
